import React from 'react';
import { Stage, Text, Layer, Rect, Transformer } from 'react-konva';
import BaseImage from 'components/baseImage';
import { IFrameOption } from 'interfaces';
import Konva from 'konva';

export interface FrameProps {
  format: string;
  imageUrl: string | null;
  layers: IFrameOption[];
  onChangeLayer: (index: any, attrs: any) => void
}

interface RectangleProps {
  shapeProps: any,
  isSelected: boolean,
  onSelect: any,
  onChange: any
}

interface ResizableTextProps {
  id:string,
  x: number,
  y: number,
  text: string,
  isSelected: boolean,
  width: number,
  height: number,
  fontSize: string,
  color: string,
  font: string,
  onSelect: any,
  onChange: any,
  align: any
}

const ResizableText: React.FC<ResizableTextProps> = ({ id, x, y, text, isSelected,
  width,
  height,
  fontSize,
  color,
  font,
  align,
  onSelect,
  onChange
}) => {
  const textRef = React.useRef<any>(null);
  const transformerRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (isSelected && transformerRef.current !== null) {
      transformerRef.current.nodes([textRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const handleResize = (id: string) => {
    if (textRef.current !== null) {
      const textNode = textRef.current;
      const newWidth = textNode.width() * textNode.scaleX();
      const newHeight = textNode.height() * textNode.scaleY();
      textNode.setAttrs({
        width: newWidth,
        height: newHeight,
        scaleX: 1
      });
      onChange(id, {
        ...textNode.attrs,
        width: newWidth,
        height: newHeight,
      });
    }
  }

  const onDragItemEnd = (evt: Konva.KonvaEventObject<DragEvent>) => {
    const textNode = textRef.current;
    onChange(id, {
      ...textNode.attrs,
      x: textNode.x(),
      y: textNode.y(),
    });
  }

  const transformer = isSelected ? (
    <Transformer
      ref={transformerRef}
      rotateEnabled={false}
      boundBoxFunc={(oldBox, newBox) => {
        newBox.width = Math.max(30, newBox.width);
        newBox.height = Math.max(40, newBox.height);
        return newBox;
      }}
    />
  ) : null;

  return (
    <>
      <Text
        draggable
        id={id}
        x={x}
        y={y}
        ref={textRef}
        text={text}
        fill={color}
        fontSize={Number.parseInt(fontSize)}
        fontFamily={font}
        perfectDrawEnabled={false}
        onDragEnd={onDragItemEnd}
        onTransformEnd={handleResize.bind(this, id)}
        onClick={onSelect}
        width={width}
        height={height}
        align={align}
        verticalAlign="middle"
      />
      {transformer}
    </>
  );
}

const Rectangle: React.FC<RectangleProps> = ({ shapeProps, isSelected, onSelect, onChange }) => {
  const shapeRef = React.useRef<any>();
  const trRef = React.useRef<any>();

  React.useEffect(() => {
    if (isSelected) {
      // we need to attach transformer manually
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <Rect
        onClick={onSelect}
        ref={shapeRef}
        {...shapeProps}
        draggable
        onDragEnd={(e) => {
          onChange({
            ...shapeProps,
            x: e.target.x(),
            y: e.target.y(),
          });
        }}
        onTransformEnd={(e) => {
          const node = shapeRef.current;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();

          // we will reset it back
          node.scaleX(1);
          node.scaleY(1);
          onChange({
            ...shapeProps,
            x: e.currentTarget.attrs.x,
            y: e.currentTarget.attrs.y,
            rotation: e.currentTarget.attrs.rotation,
            width: e.currentTarget.attrs.width * scaleX,
            height: e.currentTarget.attrs.height * scaleY,
          });
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            // limit resize
            if (newBox.width < 5 || newBox.height < 5) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
    </>
  );
};

const Frame: React.FC<FrameProps> = ({ format = "portrait", imageUrl = "", layers = [], onChangeLayer }) => {
  const size = format == "portrait" ? {
    width: 405, height: 720
  } : { width: 720, height: 405 };

  const [selectedId, selectShape] = React.useState<string | null>(null);

  const checkDeselect = (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.evt.preventDefault();
    e.evt.stopPropagation();

    // deselect when clicked on empty area
    if (e.target.className == "Image") {
      selectShape(null);
    }
  };

  return (
    <React.Fragment>
      <Stage
        className="canvas"
        width={size.width}
        height={size.height}
        onMouseDown={checkDeselect}
      >
        <BaseImage width={size.width} height={size.height} imageUrl={imageUrl}></BaseImage>
        <Layer>
          {layers.length > 0 && layers.map((item, index) => {
            switch (item.type) {
              case "text":
                return <ResizableText
                          key={index}
                          id={item.id}
                          x={item.position.x}
                          y={item.position.y}
                          width={item.size.width}
                          height={item.size.height}
                          color={item.color}
                          font={item.font}
                          fontSize={item.fontSize}
                          text={item.text}
                          align={item.align}
                          isSelected={item.id === selectedId}
                          onSelect={() => {
                            selectedId != item.id && selectShape(item.id);
                          }}
                          onChange={onChangeLayer}
                        />
              case "rect":
                return <Rectangle
                  key={item.id}
                  shapeProps={{
                    x: item.position.x,
                    y: item.position.y,
                    width: item.size?.width,
                    height: item.size?.height,
                    fill: item.color,
                    id: item.id,
                    rotation: item.rotation,
                    scaleX: 1,
                    scaleY: 1
                  }}
                  isSelected={item.id === selectedId}
                  onSelect={() => {
                    selectedId != item.id && selectShape(item.id);
                  }}
                  onChange={(newAttrs: any) => {
                    onChangeLayer(item.id, newAttrs);
                  }}
                ></Rectangle>
              default:
                return null
            };
          })
          }
        </Layer>

      </Stage>
    </React.Fragment>
  );
};

export default Frame;
